import "@fontsource/oxanium/500.css";
import "@fontsource/oxanium/600.css";
import "@fontsource/oxanium/700.css";
import "@fontsource/azeret-mono/400.css";
import "@fontsource/azeret-mono/500.css";
import "./styles.css";

import {
  MAX_TERMS,
  SAMPLE_COUNT,
  captionForTerms,
  chain,
  dft,
  fitPath,
  orderEpicycles,
  pathLength,
  resampleClosed,
  usedAmplitude,
  type Epicycle,
  type OrderMode,
  type Sample,
} from "./math";
import { PRESETS, type PresetId, presetPath } from "./shapes";

const PHOSPHOR = "#5dffc4";
const AMBER = "#ffb15a";
const BEAM = "#9fe7ff";

const canvas = document.querySelector<HTMLCanvasElement>("#scope")!;
const ctx = canvas.getContext("2d")!;
const spectrum = document.querySelector<HTMLCanvasElement>("#spectrum")!;
const sctx = spectrum.getContext("2d")!;
const stage = document.querySelector<HTMLElement>("#stage")!;
const termsInput = document.querySelector<HTMLInputElement>("#terms")!;
const termsValue = document.querySelector("#terms-value")!;
const speedInput = document.querySelector<HTMLInputElement>("#speed")!;
const speedValue = document.querySelector("#speed-value")!;
const captionEl = document.querySelector("#caption")!;
const readoutEl = document.querySelector("#readout")!;
const toastEl = document.querySelector("#toast")!;
const drawHint = document.querySelector<HTMLElement>("#draw-hint")!;
const drawBtn = document.querySelector<HTMLButtonElement>("#draw")!;
const playBtn = document.querySelector<HTMLButtonElement>("#play")!;
const stepBtn = document.querySelector<HTMLButtonElement>("#step")!;
const clearBtn = document.querySelector<HTMLButtonElement>("#clear")!;
const presetsEl = document.querySelector("#presets")!;
const togCircles = document.querySelector<HTMLButtonElement>("#tog-circles")!;
const togArms = document.querySelector<HTMLButtonElement>("#tog-arms")!;
const togTrail = document.querySelector<HTMLButtonElement>("#tog-trail")!;
const togOriginal = document.querySelector<HTMLButtonElement>("#tog-original")!;
const togOrder = document.querySelector<HTMLButtonElement>("#tog-order")!;

const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

type State = {
  source: Sample[];
  fitted: Sample[];
  samples: Sample[];
  epis: Epicycle[];
  terms: number;
  speed: number;
  t: number;
  playing: boolean;
  drawing: boolean;
  stroke: Sample[];
  trail: Sample[];
  showCircles: boolean;
  showArms: boolean;
  showTrail: boolean;
  showOriginal: boolean;
  order: OrderMode;
  preset: PresetId | "custom";
};

const state: State = {
  source: presetPath("cat"),
  fitted: [],
  samples: [],
  epis: [],
  terms: 80,
  speed: reduceMotion ? 0.12 : 0.4,
  t: 0,
  playing: !reduceMotion,
  drawing: false,
  stroke: [],
  trail: [],
  showCircles: true,
  showArms: true,
  showTrail: true,
  showOriginal: true,
  order: "amp",
  preset: "cat",
};

let toastTimer = 0;
function toast(msg: string) {
  toastEl.textContent = msg;
  toastEl.classList.add("show");
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toastEl.classList.remove("show"), 2200);
}

function viewSize() {
  const rect = canvas.getBoundingClientRect();
  return { w: Math.max(1, rect.width), h: Math.max(1, rect.height), dpr: Math.min(window.devicePixelRatio || 1, 2) };
}

function recompute() {
  const { w, h } = viewSize();
  const pad = Math.min(w, h) * 0.12;
  state.fitted = fitPath(state.source, w, h, pad);
  state.samples = resampleClosed(state.fitted, SAMPLE_COUNT);
  state.epis = orderEpicycles(dft(state.samples), state.order);
  state.trail = [];
  const max = Math.min(MAX_TERMS, Math.max(1, state.epis.length - 1));
  termsInput.max = String(max);
  if (state.terms > max) state.terms = max;
  termsInput.value = String(state.terms);
  syncLabels();
  drawSpectrum();
}

function syncLabels() {
  termsValue.textContent = String(state.terms);
  speedValue.textContent = `${state.speed.toFixed(2)}×`;
  captionEl.textContent = captionForTerms(state.terms);
  const { used, total } = usedAmplitude(state.epis, state.terms);
  const pct = total === 0 ? 0 : (100 * used) / total;
  const last = state.epis[Math.min(state.terms, state.epis.length - 1)];
  readoutEl.innerHTML = last
    ? `<span>terms <strong>${state.terms}</strong> · last f = <strong>${last.freq}</strong> · |c| = <strong>${last.amp.toFixed(2)}</strong></span>
       <span>energy kept <strong>${pct.toFixed(1)}%</strong></span>`
    : "";
  playBtn.textContent = state.playing ? "Pause" : "Play";
  playBtn.setAttribute("aria-pressed", state.playing ? "true" : "false");
  drawBtn.setAttribute("aria-pressed", state.drawing ? "true" : "false");
  drawBtn.classList.toggle("primary", state.drawing);
  drawHint.hidden = !state.drawing;
  stage.classList.toggle("playing", !state.drawing);
  togCircles.setAttribute("aria-pressed", String(state.showCircles));
  togArms.setAttribute("aria-pressed", String(state.showArms));
  togTrail.setAttribute("aria-pressed", String(state.showTrail));
  togOriginal.setAttribute("aria-pressed", String(state.showOriginal));
  togOrder.setAttribute("aria-pressed", String(state.order === "amp"));
  togOrder.textContent = state.order === "amp" ? "By size" : "By spin";
  for (const btn of presetsEl.querySelectorAll("button")) {
    btn.setAttribute("aria-pressed", String(btn.dataset.id === state.preset));
  }
}

function resize() {
  const { w, h, dpr } = viewSize();
  if (w < 2 || h < 2) return;
  const nextW = Math.floor(w * dpr);
  const nextH = Math.floor(h * dpr);
  const sr = spectrum.getBoundingClientRect();
  const sdpr = Math.min(window.devicePixelRatio || 1, 2);
  const specW = Math.max(1, Math.floor(sr.width * sdpr));
  const specH = Math.max(1, Math.floor(sr.height * sdpr));
  const canvasChanged = canvas.width !== nextW || canvas.height !== nextH;
  const specChanged = spectrum.width !== specW || spectrum.height !== specH;
  if (!canvasChanged && !specChanged) return;
  if (canvasChanged) {
    canvas.width = nextW;
    canvas.height = nextH;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  if (specChanged) {
    spectrum.width = specW;
    spectrum.height = specH;
    sctx.setTransform(sdpr, 0, 0, sdpr, 0, 0);
  }
  if (canvasChanged) recompute();
  else drawSpectrum();
}

function pointerToLocal(ev: PointerEvent): Sample {
  const r = canvas.getBoundingClientRect();
  return { x: ev.clientX - r.left, y: ev.clientY - r.top };
}

function startDraw(ev: PointerEvent) {
  state.drawing = true;
  state.playing = false;
  state.stroke = [pointerToLocal(ev)];
  state.trail = [];
  try {
    canvas.setPointerCapture(ev.pointerId);
  } catch {
    // The pointer can already be gone if the gesture was cancelled mid-event.
  }
  syncLabels();
}

function moveDraw(ev: PointerEvent) {
  if (!state.drawing || state.stroke.length === 0) return;
  const p = pointerToLocal(ev);
  const last = state.stroke[state.stroke.length - 1];
  if (Math.hypot(p.x - last.x, p.y - last.y) >= 1.6) state.stroke.push(p);
}

function endDraw() {
  if (!state.drawing) return;
  state.drawing = false;
  drawHint.hidden = true;
  if (state.stroke.length < 8 || pathLength(state.stroke) < 40) {
    toast("Draw a longer closed scribble — or pick Cat.");
    state.stroke = [];
    syncLabels();
    return;
  }
  state.source = state.stroke.slice();
  state.stroke = [];
  state.preset = "custom";
  state.t = 0;
  state.playing = !reduceMotion;
  recompute();
  toast("200 circles can redraw that. Crank the harmonics.");
}

function loadPreset(id: PresetId) {
  state.preset = id;
  state.source = presetPath(id);
  state.drawing = false;
  state.stroke = [];
  state.t = 0;
  state.playing = !reduceMotion;
  recompute();
}

function drawSpectrum() {
  const w = spectrum.getBoundingClientRect().width;
  const h = spectrum.getBoundingClientRect().height;
  sctx.clearRect(0, 0, w, h);
  if (state.epis.length === 0) return;
  const rest = state.epis.filter((e) => e.freq !== 0);
  const maxAmp = Math.max(...rest.map((e) => e.amp), 1e-6);
  const shown = rest.slice(0, 80);
  const gap = 1;
  const bw = Math.max(2, (w - 8) / shown.length - gap);
  shown.forEach((e, i) => {
    const used = i < state.terms;
    const bh = (e.amp / maxAmp) * (h - 10);
    sctx.fillStyle = used ? PHOSPHOR : "#2a3a42";
    sctx.globalAlpha = used ? 0.9 : 0.55;
    sctx.fillRect(4 + i * (bw + gap), h - 4 - bh, bw, bh);
  });
  sctx.globalAlpha = 1;
}

function paint() {
  const { w, h } = viewSize();
  ctx.clearRect(0, 0, w, h);

  const g = ctx.createRadialGradient(w * 0.5, h * 0.5, 20, w * 0.5, h * 0.55, Math.max(w, h) * 0.7);
  g.addColorStop(0, "#102018");
  g.addColorStop(1, "#05070a");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.strokeStyle = "rgba(93,255,196,0.06)";
  ctx.lineWidth = 1;
  const step = 48;
  for (let x = 24; x < w; x += step) {
    ctx.beginPath();
    ctx.moveTo(x, 16);
    ctx.lineTo(x, h - 16);
    ctx.stroke();
  }
  for (let y = 24; y < h; y += step) {
    ctx.beginPath();
    ctx.moveTo(16, y);
    ctx.lineTo(w - 16, y);
    ctx.stroke();
  }
  ctx.restore();

  if (state.drawing) {
    ctx.strokeStyle = AMBER;
    ctx.lineWidth = 2.2;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.beginPath();
    state.stroke.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.stroke();
    return;
  }

  if (state.showOriginal && state.fitted.length > 1) {
    ctx.strokeStyle = "rgba(255,177,90,0.35)";
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    state.fitted.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.stroke();
  }

  const tips = chain(state.epis, state.t, state.terms);
  if (tips.length === 0) return;

  if (state.showCircles || state.showArms) {
    ctx.save();
    for (let i = 1; i < tips.length; i++) {
      const c = tips[i - 1];
      const p = tips[i];
      const r = Math.hypot(p.x - c.x, p.y - c.y);
      if (state.showCircles && r > 0.4) {
        ctx.beginPath();
        ctx.strokeStyle = i === tips.length - 1 ? "rgba(159,231,255,0.55)" : "rgba(93,255,196,0.28)";
        ctx.lineWidth = i === tips.length - 1 ? 1.6 : 1;
        ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
        ctx.stroke();
      }
      if (state.showArms) {
        ctx.beginPath();
        ctx.strokeStyle = i === tips.length - 1 ? BEAM : "rgba(93,255,196,0.55)";
        ctx.lineWidth = i === tips.length - 1 ? 2 : 1.2;
        ctx.moveTo(c.x, c.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  if (state.showTrail && state.trail.length > 1) {
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = PHOSPHOR;
    ctx.shadowColor = PHOSPHOR;
    ctx.shadowBlur = 12;
    ctx.lineWidth = 2.2;
    ctx.globalAlpha = 0.95;
    ctx.beginPath();
    const trail = state.trail;
    ctx.moveTo(trail[0].x, trail[0].y);
    for (let i = 1; i < trail.length; i++) ctx.lineTo(trail[i].x, trail[i].y);
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;
  }

  const tip = tips[tips.length - 1];
  ctx.fillStyle = "#f5fff9";
  ctx.shadowColor = PHOSPHOR;
  ctx.shadowBlur = 18;
  ctx.beginPath();
  ctx.arc(tip.x, tip.y, 3.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
}

let last = performance.now();
function tick(now: number) {
  const dt = Math.min(0.05, (now - last) / 1000);
  last = now;
  if (state.playing && !state.drawing && state.epis.length) {
    state.t = (state.t + dt * state.speed) % 1;
    const tips = chain(state.epis, state.t, state.terms);
    const tip = tips[tips.length - 1];
    if (tip) {
      state.trail.push(tip);
      const cap = Math.floor(SAMPLE_COUNT * 1.15);
      if (state.trail.length > cap) state.trail.splice(0, state.trail.length - cap);
    }
  }
  paint();
  requestAnimationFrame(tick);
}

function bindToggles() {
  const pair = (btn: HTMLButtonElement, key: "showCircles" | "showArms" | "showTrail" | "showOriginal") => {
    btn.addEventListener("click", () => {
      state[key] = !state[key];
      syncLabels();
    });
  };
  pair(togCircles, "showCircles");
  pair(togArms, "showArms");
  pair(togTrail, "showTrail");
  pair(togOriginal, "showOriginal");
  togOrder.addEventListener("click", () => {
    state.order = state.order === "amp" ? "freq" : "amp";
    state.epis = orderEpicycles(dft(state.samples), state.order);
    state.trail = [];
    syncLabels();
    drawSpectrum();
  });
}

function mountPresets() {
  for (const p of PRESETS) {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = p.label;
    b.dataset.id = p.id;
    b.setAttribute("aria-pressed", String(p.id === state.preset));
    b.addEventListener("click", () => loadPreset(p.id));
    presetsEl.append(b);
  }
}

drawBtn.addEventListener("click", () => {
  state.drawing = !state.drawing;
  state.playing = false;
  state.stroke = [];
  syncLabels();
  if (state.drawing) toast("Scribble on the screen. Lift your pen to play.");
});
playBtn.addEventListener("click", () => {
  if (state.drawing) return;
  state.playing = !state.playing;
  syncLabels();
});
stepBtn.addEventListener("click", () => {
  state.playing = false;
  state.t = (state.t + 0.01) % 1;
  const tips = chain(state.epis, state.t, state.terms);
  const tip = tips[tips.length - 1];
  if (tip) state.trail.push(tip);
  syncLabels();
});
clearBtn.addEventListener("click", () => {
  state.trail = [];
});
termsInput.addEventListener("input", () => {
  state.terms = Number(termsInput.value);
  state.trail = [];
  syncLabels();
  drawSpectrum();
});
speedInput.addEventListener("input", () => {
  state.speed = Number(speedInput.value) / 100;
  syncLabels();
});

function blockGesture(ev: Event) {
  if (ev.cancelable) ev.preventDefault();
}

canvas.addEventListener(
  "pointerdown",
  (ev) => {
    if (ev.button !== 0) return;
    blockGesture(ev);
    startDraw(ev);
  },
  { passive: false },
);
canvas.addEventListener(
  "pointermove",
  (ev) => {
    if (state.drawing) blockGesture(ev);
    moveDraw(ev);
  },
  { passive: false },
);
canvas.addEventListener("pointerup", endDraw);
canvas.addEventListener("pointercancel", endDraw);
canvas.addEventListener("touchstart", blockGesture, { passive: false });
canvas.addEventListener("touchmove", blockGesture, { passive: false });

window.addEventListener("keydown", (ev) => {
  const tag = (ev.target as HTMLElement | null)?.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA") return;
  if (ev.code === "Space") {
    ev.preventDefault();
    if (!state.drawing) {
      state.playing = !state.playing;
      syncLabels();
    }
  } else if (ev.key === "d" || ev.key === "D") {
    drawBtn.click();
  } else if (ev.key === "[") {
    state.terms = Math.max(1, state.terms - 1);
    termsInput.value = String(state.terms);
    state.trail = [];
    syncLabels();
    drawSpectrum();
  } else if (ev.key === "]") {
    const max = Number(termsInput.max);
    state.terms = Math.min(max, state.terms + 1);
    termsInput.value = String(state.terms);
    state.trail = [];
    syncLabels();
    drawSpectrum();
  } else if (/^[1-8]$/.test(ev.key)) {
    const p = PRESETS[Number(ev.key) - 1];
    if (p) loadPreset(p.id);
  }
});

speedInput.value = String(Math.round(state.speed * 100));
mountPresets();
bindToggles();
const wanted = new URLSearchParams(location.search).get("preset") as PresetId | "custom" | null;
if (wanted && wanted !== "custom" && PRESETS.some((p) => p.id === wanted)) {
  state.preset = wanted;
  state.source = presetPath(wanted);
}
resize();
window.addEventListener("resize", resize);
window.visualViewport?.addEventListener("resize", resize);
const layoutObserver = new ResizeObserver(() => resize());
layoutObserver.observe(stage);
layoutObserver.observe(spectrum);
document.fonts?.ready.then(() => resize());
syncLabels();
requestAnimationFrame(tick);
